/**
 * Shared presentational helpers for dashboard widgets.
 *
 * Absence/probation date fields arrive as date-only "YYYY-MM-DD" strings (the backend
 * slices `@db.Date` values with `.toISOString().slice(0, 10)`), so these parse the
 * calendar day directly into a local `Date` — never `new Date("YYYY-MM-DD")`, which
 * parses as UTC midnight and drifts the day for viewers west of UTC.
 *
 * Every helper here takes whatever the response actually carried, not what its type
 * promises: a dashboard renders a dozen independent payloads side by side, and a field
 * that turned nullable server-side must degrade to a placeholder in one row instead of
 * throwing through the whole grid.
 */

/**
 * A date-only string, or a full timestamp that drifted in — but nothing else: the tail is
 * anchored so a truncated value ("2026-08-211") is rejected instead of rendering a plausible
 * wrong day.
 */
const CALENDAR_DAY = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s]\d.*)?$/;

/** Rendered in place of a date we cannot read. */
const NO_VALUE = '—';

/**
 * Parse the calendar day of a date-only "YYYY-MM-DD" string into a local-midnight Date
 * without timezone drift. Returns null when the value is missing or unreadable — including
 * a real month/day that does not exist, which `new Date` would silently roll over.
 */
export function toLocalDate(iso: string | null | undefined): Date | null {
  if (typeof iso !== 'string') return null;
  const match = CALENDAR_DAY.exec(iso);
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const date = new Date(y, m - 1, d);
  // A month or day out of range rolls over silently (`2026-02-30` becomes March 2), and a
  // two-digit year is remapped into the 1900s, so the day is only accepted if all three parts
  // survive the round trip.
  const survives = date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
  return survives ? date : null;
}

/** Format a date-only "YYYY-MM-DD" string as "Aug 3". */
export function formatDay(iso: string | null | undefined): string {
  const date = toLocalDate(iso);
  return date ? monthDay(date) : NO_VALUE;
}

/**
 * "Jul 14" · "Jul 14–16" (same month) · "Jul 30 – Aug 2" (cross-month).
 * An unreadable end keeps its side of the range as a placeholder rather than collapsing to
 * the other one: "— – Jul 16" says the start is unknown, where "Jul 16" would read as a
 * one-day absence the data does not support.
 */
export function formatRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined
): string {
  const s = toLocalDate(startIso);
  const e = toLocalDate(endIso);
  if (!s && !e) return NO_VALUE;
  if (!s) return `${NO_VALUE} – ${monthDay(e as Date)}`;
  if (!e) return `${monthDay(s)} – ${NO_VALUE}`;
  // Compare the parsed days, not the raw strings: the same day can arrive in two shapes
  // ("2026-07-14" and "2026-07-14T00:00:00.000Z") and is one day either way.
  if (s.getTime() === e.getTime()) return monthDay(s);
  const sameMonth = s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth();
  return sameMonth ? `${monthDay(s)}–${e.getDate()}` : `${monthDay(s)} – ${monthDay(e)}`;
}

function monthDay(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * A list field of a widget response as a list of rows worth rendering. A field that arrived
 * missing, or as something other than an array, becomes empty rather than throwing on
 * `.map`/spread — `value.length === 0` does not catch either case (`undefined === 0` is false).
 * Rows that are not there are dropped, so a caller's own emptiness check and its "+ N more"
 * count both speak for the rows it can actually draw.
 */
export function asArray<T>(value: readonly T[] | null | undefined): readonly T[] {
  if (!Array.isArray(value)) return [];
  return value.some((row) => row == null) ? value.filter((row) => row != null) : value;
}

/** Two-letter initials from a candidate's full name, for the avatar fallback. */
export function candidateInitials(name: string | null | undefined): string {
  if (typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
